import React from 'react';

const AREA_OPTIONS: { key: string; label: string; sqftPerEmployee: number }[] = [
    { key: 'economy', label: 'Economy – 100 Sq.Ft.', sqftPerEmployee: 100 },
    { key: 'standard', label: 'Standard – 135 Sq.Ft.', sqftPerEmployee: 135 },
    { key: 'premium', label: 'Premium – 200 Sq.Ft.', sqftPerEmployee: 200 }
];

const AMENITY_PRESETS: { key: string; label: string; sqftPerUnit: number }[] = [
    { key: 'cafeteria', label: 'Cafeteria (20 People)', sqftPerUnit: 250 },
    { key: 'pantry', label: 'Pantry', sqftPerUnit: 50 },
    { key: 'cabin_economy', label: 'Cabin - Economy Fit', sqftPerUnit: 80 },
    { key: 'cabin_medium', label: 'Cabin - Medium Fit', sqftPerUnit: 100 },
    { key: 'cabin_large', label: 'Cabin - Large Fit', sqftPerUnit: 150 },
    { key: 'meeting', label: 'Meeting Room', sqftPerUnit: 200 },
    { key: 'board', label: 'Board Room', sqftPerUnit: 300 }
];

const AMENITY_COLORS: Record<string, string> = {
    cafeteria: '#f1ae24',      // gold
    pantry: '#10b981',         // emerald
    cabin_economy: '#ec4899',  // pink
    cabin_medium: '#8b5cf6',   // violet/purple
    cabin_large: '#3b82f6',    // blue
    meeting: '#f97316',        // orange
    board: '#06b6d4'           // cyan
};

const OfficeSpaceCalculator: React.FC = () => {
    const [employees, setEmployees] = React.useState<number>(10);
    const [areaKey, setAreaKey] = React.useState<string>('economy');
    const [amenities, setAmenities] = React.useState<Record<string, number>>(() => {
        const init: Record<string, number> = {};
        AMENITY_PRESETS.forEach(a => (init[a.key] = 0));
        return init;
    });

    const perEmployee = AREA_OPTIONS.find((o) => o.key === areaKey)?.sqftPerEmployee ?? 100;

    const workspaceArea = employees * perEmployee;

    const amenityAreas = React.useMemo(() => {
        const map: Record<string, number> = {};
        AMENITY_PRESETS.forEach(a => {
            const cnt = amenities[a.key] || 0;
            map[a.key] = cnt * a.sqftPerUnit;
        });
        return map;
    }, [amenities]);

    const amenitiesTotal = Object.values(amenityAreas).reduce((s, v) => s + v, 0);
    const totalSqft = workspaceArea + amenitiesTotal;

    const slices = React.useMemo(() => {
        const parts: { label: string; value: number; color: string }[] = [];
        // workspace first (dark blue)
        if (workspaceArea > 0) parts.push({ label: 'Workspace Area', value: workspaceArea, color: '#0b3b82' });
        AMENITY_PRESETS.forEach((a) => {
            const v = amenityAreas[a.key] || 0;
            if (v > 0) {
                parts.push({ label: a.label, value: v, color: AMENITY_COLORS[a.key] || '#6B7280' });
            }
        });
        return parts;
    }, [workspaceArea, amenityAreas]);

    const donutStyle = React.useMemo(() => {
        if (totalSqft <= 0) return { background: '#f1ae24' } as React.CSSProperties;
        let start = 0;
        const segments = slices.map(s => {
            const perc = (s.value / totalSqft) * 100;
            const seg = `${s.color} ${start}% ${start + perc}%`;
            start += perc;
            return seg;
        });
        const css = { background: `conic-gradient(${segments.join(',')})` } as React.CSSProperties;
        return css;
    }, [slices, totalSqft]);

    const changeAmenity = (key: string, delta: number) => {
        setAmenities(prev => ({ ...prev, [key]: Math.max(0, (prev[key] || 0) + delta) }));
    };

    const handleQuickAdd = (key: string) => {
        changeAmenity(key, 1);
    };

    return (
        <div className="bg-white rounded-[2rem] border border-slate-200 p-6 md:p-10 purple-card-shadow">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                {/* Left Column: Inputs */}
                <div className="lg:col-span-7 space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div className="space-y-2">
                            <label className="block text-sm font-bold text-slate-800">Number of Employees*</label>
                            <input 
                                type="number" 
                                min="1"
                                value={employees} 
                                onChange={(e) => setEmployees(Math.max(1, Number(e.target.value) || 1))} 
                                className="w-full border border-slate-200 rounded-2xl p-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#001b4a] transition-all font-semibold" 
                            />
                        </div>

                        <div className="space-y-2">
                            <label className="block text-sm font-bold text-slate-800">Area Configuration*</label>
                            <select 
                                value={areaKey} 
                                onChange={(e) => setAreaKey(e.target.value)} 
                                className="w-full border border-slate-200 rounded-2xl p-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#001b4a] transition-all font-semibold bg-white"
                            >
                                {AREA_OPTIONS.map(o => (
                                    <option key={o.key} value={o.key}>{o.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Quick Add */}
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-2.5">Quick Add Amenities</label>
                        <div className="flex flex-wrap gap-2">
                            {AMENITY_PRESETS.map(a => {
                                const count = amenities[a.key] || 0;
                                return (
                                    <button 
                                        key={a.key} 
                                        type="button" 
                                        onClick={() => handleQuickAdd(a.key)}
                                        className={`px-3.5 py-2 rounded-full text-xs font-bold border transition-all flex items-center gap-1.5 shadow-sm active:scale-95 ${
                                            count > 0 
                                                ? 'bg-[#001b4a] text-white border-[#001b4a]' 
                                                : 'bg-slate-50 text-slate-600 border-slate-200 hover:border-slate-400'
                                        }`}
                                    >
                                        <span>{a.label.split(' ')[0]}</span>
                                        {count > 0 && <span className="bg-[#f1ae24] text-[#001b4a] w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-extrabold">{count}</span>}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Increment List */}
                    <div className="space-y-3.5">
                        <label className="block text-sm font-bold text-slate-800">Fine-tune Amenities</label>
                        <div className="grid grid-cols-1 gap-2.5 max-h-[380px] overflow-y-auto pr-1.5 custom-scrollbar">
                            {AMENITY_PRESETS.map(a => (
                                <div key={a.key} className="flex items-center justify-between p-3.5 rounded-2xl border border-slate-100 bg-slate-50/50 hover:bg-slate-50 transition-all">
                                    <div>
                                        <div className="text-sm font-bold text-slate-800">{a.label}</div>
                                        <div className="text-xs text-slate-400 font-bold mt-0.5">+{a.sqftPerUnit} Sq.Ft. per unit</div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <button 
                                            type="button" 
                                            onClick={() => changeAmenity(a.key, -1)} 
                                            className="w-8 h-8 rounded-full border border-slate-200 bg-white flex items-center justify-center font-bold text-slate-600 hover:bg-slate-100 active:scale-95 transition-all shadow-sm"
                                        >
                                            -
                                        </button>
                                        <div className="w-8 text-center text-sm font-bold text-slate-800">{amenities[a.key] || 0}</div>
                                        <button 
                                            type="button" 
                                            onClick={() => changeAmenity(a.key, 1)} 
                                            className="w-8 h-8 rounded-full border border-slate-200 bg-white flex items-center justify-center font-bold text-slate-600 hover:bg-slate-100 active:scale-95 transition-all shadow-sm"
                                        >
                                            +
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Right Column: Premium Summary */}
                <div className="lg:col-span-5 lg:sticky lg:top-6">
                    <div className="rounded-[2rem] border-8 border-[#001b4a] p-6 md:p-8 bg-white shadow-xl relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-slate-50 rounded-full blur-2xl opacity-40 pointer-events-none"></div>
                        
                        <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-100">
                            <div>
                                <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Total Required Space</span>
                                <h3 className="font-black text-3xl text-[#001b4a] tracking-tight mt-0.5">{totalSqft} <span className="text-lg font-bold text-slate-500">Sq.Ft.</span></h3>
                            </div>
                            <span className="px-3 py-1 rounded-full bg-emerald-50 text-emerald-600 text-xs font-bold border border-emerald-100">Calculated</span>
                        </div>

                        <div className="flex flex-col items-center gap-6">
                            {/* Doughnut Chart */}
                            <div style={{ width: 180, height: 180, borderRadius: 9999, ...donutStyle }} className="relative flex items-center justify-center shadow-inner shrink-0">
                                <div style={{ width: 126, height: 126, borderRadius: 9999, background: '#fff' }} className="absolute flex flex-col items-center justify-center shadow-md">
                                    <span className="text-[10px] text-slate-400 uppercase font-black tracking-widest leading-none mb-1">Total Space</span>
                                    <span className="text-2xl text-[#001b4a] font-black leading-none">{Math.round(totalSqft)}</span>
                                    <span className="text-[10px] text-slate-500 font-bold leading-none mt-1">Sq.Ft.</span>
                                </div>
                            </div>

                            {/* Legend / Breakdown List */}
                            <div className="w-full border-t border-slate-100 pt-5 mt-2">
                                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 text-center sm:text-left">Area Breakdown</h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-2.5">
                                    {/* Workspace */}
                                    <div className="flex items-center justify-between p-3 rounded-2xl bg-slate-50/50 hover:bg-slate-50 transition-all border border-slate-100/50">
                                        <div className="flex items-center gap-3">
                                            <span className="inline-block w-3.5 h-3.5 bg-[#0b3b82] rounded-md shrink-0 shadow-sm"></span>
                                            <div className="text-xs font-bold text-slate-700">Workspace Area</div>
                                        </div>
                                        <div className="font-extrabold text-xs text-[#001b4a]">{workspaceArea} Sq.Ft.</div>
                                    </div>

                                    {/* Amenities */}
                                    {AMENITY_PRESETS.map(a => (
                                        amenities[a.key] > 0 ? (
                                            <div key={a.key} className="flex items-center justify-between p-3 rounded-2xl bg-slate-50/50 hover:bg-slate-50 transition-all border border-slate-100/50">
                                                <div className="flex items-center gap-3">
                                                    <span className="inline-block w-3.5 h-3.5 rounded-md shrink-0 shadow-sm" style={{ backgroundColor: AMENITY_COLORS[a.key] || '#6B7280' }}></span>
                                                    <div className="text-xs font-bold text-slate-700">{a.label.split(' ')[0]} ({amenities[a.key]}x)</div>
                                                </div>
                                                <div className="font-bold text-xs text-[#001b4a]">{(amenities[a.key] * a.sqftPerUnit)} Sq.Ft.</div>
                                            </div>
                                        ) : null
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OfficeSpaceCalculator;
